import { useCallback, useMemo } from 'react'
import { Seo } from 'components'
import { KDVID_FRONT_INDIVIDUAL_URL } from 'constants/global'
import { getFormatPhoneNumber, getFullNameFromFio } from 'helpers/string'
import IconRestart from 'remixicon/icons/Device/restart-line.svg?react'
import IconSpam from 'remixicon/icons/System/spam-line.svg?react'
import { Button } from 'shared/components'
import Widget from 'src/tailwind-components/extends/Widget/Widget'
import { useTypedSelector } from 'src/hooks'
import { useUserInfoQuery } from 'src/modules/login/api'

import { useGetUserDocumentTypesQuery } from '../api'
import { ProfileDocument } from '../components/ProfileDocument'
import { ProfileInfo } from '../components/ProfileInfo'

export const ProfilePage = () => {
  const { token } = useTypedSelector((state) => state.authReducer)
  const { data, isLoading: isLoadingUserInfo, isFetching, isError } = useUserInfoQuery(undefined, { skip: !token })

  const { document, firstname, lastname, secondname, email, phone } = data || {}

  const { data: documentType, isLoading: isLoadingUserDocumentTypes } = useGetUserDocumentTypesQuery(undefined, {
    skip: isLoadingUserInfo || !document?.length
  })

  const handleReload = useCallback(() => window.location.reload(), [])

  const page = useMemo(() => {
    if (isError && !data) {
      return (
        <>
          <div className="mt-6 grid grid-cols-[auto,1fr] gap-x-4 pb-6">
            <IconSpam className="text-on-surface-variant" />
            <div>
              <div className="text-title-sm">Не удалось получить данные от сервиса KDV ID</div>
              <div className="text-body-md text-on-surface-variant">
                Возможно проблемы со связью — попробуйте повторить запрос через некоторое время.
              </div>
              <div className="text-body-md text-on-surface-variant">Приносим извинения за временные неудобства.</div>
            </div>
          </div>
          <Button
            className="mt-6"
            onClick={handleReload}
            prefix={<IconRestart size="xs" />}
            size="lg"
            variant="outline"
          >
            Повторить
          </Button>
        </>
      )
    }

    return (
      <>
        <ProfileInfo
          email={email}
          isLoading={isFetching || isLoadingUserInfo || isLoadingUserDocumentTypes}
          name={getFullNameFromFio({ lastname, firstname, secondname })}
          phone={getFormatPhoneNumber(phone)}
        />

        {document && Boolean(document?.length) && (
          <>
            <div className="mt-6 text-headline-sm">Документ, удостоверяющий личность</div>
            <ProfileDocument
              document={document}
              documentType={documentType}
              isLoading={isFetching || isLoadingUserDocumentTypes}
            />
          </>
        )}

        <Button className="mt-6" href={KDVID_FRONT_INDIVIDUAL_URL} size="lg" target="_blank" variant="outline">
          Редактировать в KDV ID
        </Button>
        <div className="mt-4 max-w-screen-1md text-body-sm text-on-surface-variant">
          Данные получены из KDV ID. Это сервис для надежной авторизации для российских сайтов и приложений.
          Пользователи с учётной записью в KDV ID получают быстрый вход без ввода данных и могут сразу же начать
          работать.
        </div>
        <div className="mt-2 text-body-sm text-on-surface-variant">
          <b>Не видите часть информации?</b> Перейдите на сервис KDV ID и предоставьте соответствующий доступ для сайта
          QP5.
        </div>
      </>
    )
  }, [
    data,
    document,
    documentType,
    email,
    firstname,
    handleReload,
    isError,
    isFetching,
    isLoadingUserDocumentTypes,
    isLoadingUserInfo,
    lastname,
    phone,
    secondname
  ])

  return (
    <>
      <Seo
        description="Персональные данные о клиенте сервиса QP5 Express для связи по рабочим вопросам: фамилия и имя заказчика, контактный номер телефона."
        title="Персональная информация – сборные перевозки QP5 Express"
      />
      <div className="relative mx-auto my-0 flex w-full max-w-[1400px] items-center justify-center p-4 px-4 py-8 max-2lg:flex max-2lg:flex-col max-3md:px-0 max-3md:pb-12 max-3md:pt-10 max-2md:w-auto">
        <Widget>
          <div className="relative" data-test-id="order--table">
            <div className="flex flex-col p-4 pb-0 max-md:flex-col">
              <div className="flex w-full items-center justify-between pb-4 max-lg:pb-0 max-md:flex max-md:w-full max-md:justify-between">
                <h2 className="text-headline-md">Учётная запись</h2>
              </div>
            </div>
            <div className="p-4 pt-0">{page}</div>
          </div>
        </Widget>
      </div>
    </>
  )
}
